import { state } from './state.js';
import * as API from './api.js';

const CARD_CONFIG = {
    history: {
        stateKey: 'historyCollapsed',
        cardId: 'historyCard',
        toggleId: 'historyToggle',
        summaryId: 'historyCollapsedSummary',
    },
    create: {
        stateKey: 'createMosaicCollapsed',
        cardId: 'createMosaicCard',
        toggleId: 'createMosaicToggle',
        summaryId: 'createMosaicCollapsedSummary',
    },
    main: {
        stateKey: 'mainPhotoCollapsed',
        cardId: 'mainPhotoCard',
        toggleId: 'mainPhotoToggle',
        summaryId: 'mainPhotoCollapsedSummary',
    },
    library: {
        stateKey: 'tileLibraryCollapsed',
        cardId: 'tileLibraryCard',
        toggleId: 'tileLibraryToggle',
        summaryId: 'tileLibraryCollapsedSummary',
    },
    settings: {
        stateKey: 'settingsCollapsed',
        cardId: 'configCard',
        toggleId: 'settingsToggle',
        summaryId: 'settingsCollapsedSummary',
    },
    viewer: {
        stateKey: 'viewerCollapsed',
        cardId: 'resultCard',
        toggleId: 'resultToggle',
        summaryId: 'resultCollapsedSummary',
    },
};

const CARD_TRANSITION_DURATION_MS = 220;
const CARD_TRANSITION_FALLBACK_MS = 380;
const WORKFLOW_SCROLL_DEFAULT_DURATION_MS = 700;
const WORKFLOW_STAGE_ORDER = ['history', 'create', 'viewer'];
const WORKFLOW_STAGE_BY_TARGET = {
    history: 'history',
    create: 'create',
    main: 'create',
    library: 'create',
    settings: 'create',
    viewer: 'viewer',
};
const WORKFLOW_STAGE_HASH_PREFIX = '#stage=';
const WORKFLOW_STAGE_TRANSITION_FALLBACK_MS = 560;
const WORKFLOW_STAGE_TRANSITION_DEFAULT_MS = 420;
const CREATE_FLOW_PANEL_ANIM_MS = 260;
const CREATE_FLOW_ORDER = ['main', 'library', 'settings'];
const CREATE_FLOW_BUTTON_BY_STEP = {
    main: 'createStepMainBtn',
    library: 'createStepLibraryBtn',
    settings: 'createStepSettingsBtn',
};
const CREATE_FLOW_CARD_BY_STEP = {
    main: 'mainPhotoCard',
    library: 'tileLibraryCard',
    settings: 'configCard',
};

let _workflowTransitionLocked = false;
let _scrollRequestToken = 0;
let _scrollRafId = 0;
let _scrollTimeoutId = 0;
let _scrollAnimRafId = 0;
let _scrollAnimToken = 0;
let _scrollAnimResolve = null;
let _workflowScrollInteractionLocked = false;
let _compactStripResizeBound = false;
let _compactStripResizeRaf = 0;
let _compactStripResizeObserver = null;
let _workflowStage = 'history';
let _workflowStageTransitioning = false;
let _workflowStageTransitionToken = 0;
let _workflowStageTransitionTimer = 0;
let _workflowStageListenersBound = false;
let _createFlowStep = 'main';
let _createFlowHeaderBound = false;
let _createFlowNextBound = false;
let _createFlowAnimTimer = 0;

const COMPACT_STRIP_DEFAULT_TILE_PX = 68;
const COMPACT_STRIP_DEFAULT_GAP_PX = 6;

function prefersReducedMotion() {
    if (typeof window.matchMedia !== 'function') return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function normalizeWorkflowStage(stage) {
    const normalized = String(stage || '').trim().toLowerCase();
    return WORKFLOW_STAGE_ORDER.includes(normalized) ? normalized : '';
}

function resolveWorkflowStageForTarget(target) {
    if (typeof target !== 'string') return '';
    const key = String(target || '').trim().toLowerCase();
    return WORKFLOW_STAGE_BY_TARGET[key] || '';
}

function getWorkflowStageTrack() {
    return document.getElementById('workflowStageTrack');
}

function getWorkflowStagePanels() {
    return Array.from(document.querySelectorAll('.workflow-stage-panel[data-stage]'));
}

function getWorkflowStageIndex(stage) {
    const idx = WORKFLOW_STAGE_ORDER.indexOf(stage);
    return idx >= 0 ? idx : 0;
}

function setBodyStageClass(stage) {
    if (!document.body) return;
    WORKFLOW_STAGE_ORDER.forEach((name) => {
        document.body.classList.toggle(`workflow-stage-${name}`, name === stage);
    });
}

function setStagePanelA11y(stage) {
    const panels = getWorkflowStagePanels();
    if (!panels.length) return;
    panels.forEach((panel) => {
        const panelStage = normalizeWorkflowStage(panel.dataset.stage || '');
        const active = panelStage === stage;
        panel.classList.toggle('is-active', active);
        panel.setAttribute('aria-hidden', active ? 'false' : 'true');
        if ('inert' in panel) {
            panel.inert = !active;
        } else if (!active) {
            panel.setAttribute('inert', '');
        } else {
            panel.removeAttribute('inert');
        }
    });
}

function setWorkflowStageCssIndex(stage) {
    const track = getWorkflowStageTrack();
    if (!track) return;
    const index = getWorkflowStageIndex(stage);
    track.style.setProperty('--workflow-stage-index', String(index));
}

function focusWorkflowStagePrimaryControl(stage, explicitId = '') {
    const direct = explicitId ? document.getElementById(explicitId) : null;
    if (direct && typeof direct.focus === 'function') {
        direct.focus({ preventScroll: true });
        return;
    }
    const fallbackId = ({
        history: 'historyToggle',
        create: 'createStepMainBtn',
        viewer: 'resultCard',
    })[stage] || '';
    if (!fallbackId) return;
    const target = document.getElementById(fallbackId);
    if (target && typeof target.focus === 'function') {
        target.focus({ preventScroll: true });
    }
}

function readStageFromHash() {
    if (typeof window === 'undefined') return '';
    const rawHash = String(window.location.hash || '').trim();
    if (!rawHash) return '';
    if (rawHash.startsWith(WORKFLOW_STAGE_HASH_PREFIX)) {
        return normalizeWorkflowStage(rawHash.slice(WORKFLOW_STAGE_HASH_PREFIX.length));
    }
    const normalizedHash = rawHash.startsWith('#') ? rawHash.slice(1) : rawHash;
    return normalizeWorkflowStage(normalizedHash);
}

function writeStageToHash(stage, options = {}) {
    if (typeof window === 'undefined' || !window.history) return;
    const safeStage = normalizeWorkflowStage(stage);
    if (!safeStage) return;
    const pushHistory = Boolean(options.pushHistory);
    const nextHash = `${WORKFLOW_STAGE_HASH_PREFIX}${safeStage}`;
    if (window.location.hash === nextHash) return;
    try {
        const nextUrl = new URL(window.location.href);
        nextUrl.hash = nextHash;
        if (pushHistory) {
            window.history.pushState({}, document.title, nextUrl.toString());
        } else {
            window.history.replaceState({}, document.title, nextUrl.toString());
        }
    } catch (_e) {}
}

function clearWorkflowStageTransitionTimer() {
    if (_workflowStageTransitionTimer) {
        window.clearTimeout(_workflowStageTransitionTimer);
        _workflowStageTransitionTimer = 0;
    }
}

function setWorkflowStageTransitioning(transitioning) {
    const next = Boolean(transitioning);
    _workflowStageTransitioning = next;
    const track = getWorkflowStageTrack();
    if (track) track.classList.toggle('is-transitioning', next);
    if (document.body) {
        document.body.classList.toggle('workflow-stage-transitioning', next);
    }
}

function isWorkflowStageTransitioning() {
    return _workflowStageTransitioning;
}

export function getWorkflowStage() {
    return _workflowStage;
}

export function setWorkflowStage(stage, options = {}) {
    return transitionWorkflowStage(stage, options);
}

function logStageTransition(fromStage, toStage, source, animated, durationMs) {
    API.logUiFlow('stage', 'transition', 'ok', {
        from_stage: fromStage,
        to_stage: toStage,
        source: String(source || ''),
        animated: Boolean(animated),
        duration_ms: Math.max(0, Math.round(Number(durationMs || 0))),
    }).catch(() => {});
}

async function transitionWorkflowStage(stage, options = {}) {
    const nextStage = normalizeWorkflowStage(stage);
    if (!nextStage) return false;
    const currentStage = normalizeWorkflowStage(_workflowStage) || 'history';
    if (currentStage === nextStage && !isWorkflowStageTransitioning()) {
        setWorkflowStageCssIndex(nextStage);
        setStagePanelA11y(nextStage);
        setBodyStageClass(nextStage);
        if (options.syncHash !== false) {
            writeStageToHash(nextStage, { pushHistory: Boolean(options.pushHistory) });
        }
        return false;
    }

    const animate = options.animate !== false && !prefersReducedMotion();
    const source = String(options.source || '');
    const durationMs = Math.max(
        1,
        Number(options.durationMs || WORKFLOW_STAGE_TRANSITION_DEFAULT_MS),
    );
    const token = ++_workflowStageTransitionToken;

    setWorkflowTransitionLock(true);
    setWorkflowStageTransitioning(true);
    emitWorkflowSequenceEvent('stage_transition_start', {
        source,
        from_stage: currentStage,
        to_stage: nextStage,
    });

    const finish = () => {
        if (token !== _workflowStageTransitionToken) return;
        clearWorkflowStageTransitionTimer();
        _workflowStage = nextStage;
        setWorkflowStageCssIndex(nextStage);
        setStagePanelA11y(nextStage);
        setBodyStageClass(nextStage);
        setWorkflowStageTransitioning(false);
        setWorkflowTransitionLock(false);
        emitWorkflowSequenceEvent('stage_transition_end', {
            source,
            from_stage: currentStage,
            to_stage: nextStage,
        });
        if (options.syncHash !== false) {
            writeStageToHash(nextStage, { pushHistory: Boolean(options.pushHistory) });
        }
        if (!options.skipLog) {
            logStageTransition(currentStage, nextStage, source, animate, durationMs);
        }
        if (options.focus !== false) {
            focusWorkflowStagePrimaryControl(nextStage, String(options.focusTargetId || ''));
        }
    };

    if (!animate) {
        finish();
        return true;
    }

    await new Promise((resolve) => {
        const track = getWorkflowStageTrack();
        if (!track) {
            resolve();
            return;
        }
        let settled = false;
        const settle = () => {
            if (settled) return;
            settled = true;
            track.removeEventListener('transitionend', onEnd);
            clearWorkflowStageTransitionTimer();
            resolve();
        };
        const onEnd = (event) => {
            if (event.target !== track) return;
            if (event.propertyName !== 'transform') return;
            settle();
        };
        track.addEventListener('transitionend', onEnd);
        _workflowStageTransitionTimer = window.setTimeout(
            settle,
            Math.max(durationMs + 120, WORKFLOW_STAGE_TRANSITION_FALLBACK_MS),
        );
        window.requestAnimationFrame(() => {
            if (token !== _workflowStageTransitionToken) {
                settle();
                return;
            }
            setWorkflowStageCssIndex(nextStage);
        });
    });

    finish();
    return true;
}

function bindWorkflowStageListeners() {
    if (_workflowStageListenersBound) return;
    _workflowStageListenersBound = true;
    window.addEventListener('popstate', () => {
        const stageFromHash = readStageFromHash() || 'history';
        transitionWorkflowStage(stageFromHash, {
            animate: false,
            source: 'history_nav',
            syncHash: false,
            skipLog: true,
        }).catch(() => {});
    });
}

function initWorkflowStageState() {
    bindWorkflowStageListeners();
    const initialStage = readStageFromHash() || 'history';
    _workflowStage = normalizeWorkflowStage(initialStage) || 'history';
    setWorkflowStageCssIndex(_workflowStage);
    setStagePanelA11y(_workflowStage);
    setBodyStageClass(_workflowStage);
    writeStageToHash(_workflowStage, { pushHistory: false });
}

function normalizeCreateFlowStep(step) {
    const normalized = String(step || '').trim().toLowerCase();
    return CREATE_FLOW_ORDER.includes(normalized) ? normalized : '';
}

function getCreateFlowStepIndex(step) {
    const idx = CREATE_FLOW_ORDER.indexOf(normalizeCreateFlowStep(step));
    return idx >= 0 ? idx : 0;
}

function isCreateFlowNonCollapsible(name) {
    return name === 'create' || name === 'main' || name === 'library' || name === 'settings' || name === 'viewer';
}

function getCreateFlowMaxAllowedIndex() {
    const mainReady = hasMainPhotoSelection();
    if (!mainReady) return 0;
    const selectedLibrary = getSelectedLibrary();
    if (!libraryHasTiles(selectedLibrary)) return 1;
    return 2;
}

function resolveCreateFlowStepFromSelections() {
    const maxIdx = getCreateFlowMaxAllowedIndex();
    return CREATE_FLOW_ORDER[maxIdx] || 'main';
}

function isCreateFlowStepTarget(step) {
    return Boolean(normalizeCreateFlowStep(step));
}

function updateCreateFlowHeader() {
    const activeStep = normalizeCreateFlowStep(_createFlowStep) || 'main';
    const maxAllowedIdx = getCreateFlowMaxAllowedIndex();
    CREATE_FLOW_ORDER.forEach((stepName) => {
        const btn = document.getElementById(CREATE_FLOW_BUTTON_BY_STEP[stepName]);
        if (!btn) return;
        const idx = getCreateFlowStepIndex(stepName);
        const isActive = stepName === activeStep;
        const isLocked = idx > maxAllowedIdx;
        const isDone = !isActive && idx < maxAllowedIdx;
        btn.classList.toggle('is-active', isActive);
        btn.classList.toggle('is-done', isDone);
        btn.classList.toggle('is-locked', isLocked);
        btn.disabled = isLocked;
        btn.setAttribute('aria-disabled', isLocked ? 'true' : 'false');
        btn.setAttribute('aria-selected', isActive ? 'true' : 'false');
        btn.setAttribute('tabindex', isActive ? '0' : '-1');
    });
}

function getCreateFlowNextState() {
    const currentStep = normalizeCreateFlowStep(_createFlowStep) || 'main';
    if (currentStep === 'main') {
        if (!hasMainPhotoSelection()) return null;
        return {
            nextStep: 'library',
            label: 'Next: Choose Tile Library',
        };
    }
    if (currentStep === 'library') {
        if (!hasSelectedLibraryWithTiles()) return null;
        return {
            nextStep: 'settings',
            label: 'Next: Mosaic Configuration',
        };
    }
    return null;
}

function clearCreateFlowPanelAnimation() {
    if (_createFlowAnimTimer) {
        window.clearTimeout(_createFlowAnimTimer);
        _createFlowAnimTimer = 0;
    }
    CREATE_FLOW_ORDER.forEach((stepName) => {
        const panel = document.getElementById(CREATE_FLOW_CARD_BY_STEP[stepName]);
        if (!panel) return;
        panel.classList.remove('is-enter-from-right', 'is-enter-from-left');
    });
}

function animateCreateFlowPanelEntry(fromStep, toStep, options = {}) {
    if (options.animate === false || prefersReducedMotion()) return;
    const fromIdx = getCreateFlowStepIndex(fromStep);
    const toIdx = getCreateFlowStepIndex(toStep);
    if (fromIdx === toIdx) return;
    const panel = document.getElementById(CREATE_FLOW_CARD_BY_STEP[toStep]);
    if (!panel) return;
    clearCreateFlowPanelAnimation();
    const enteringClass = toIdx > fromIdx ? 'is-enter-from-right' : 'is-enter-from-left';
    panel.classList.add(enteringClass);
    _createFlowAnimTimer = window.setTimeout(() => {
        _createFlowAnimTimer = 0;
        panel.classList.remove(enteringClass);
    }, CREATE_FLOW_PANEL_ANIM_MS + 40);
}

function updateCreateFlowNextButton() {
    const nextBtn = document.getElementById('createStepNextBtn');
    if (!nextBtn) return;
    const nextState = getCreateFlowNextState();
    if (!nextState) {
        nextBtn.classList.add('hidden');
        nextBtn.disabled = true;
        nextBtn.dataset.nextStep = '';
        return;
    }
    nextBtn.classList.remove('hidden');
    nextBtn.disabled = false;
    nextBtn.dataset.nextStep = nextState.nextStep;
    nextBtn.textContent = nextState.label;
}

function setCreateFlowPanelState(stepName, active) {
    const card = document.getElementById(CREATE_FLOW_CARD_BY_STEP[stepName]);
    if (!card) return;
    card.classList.toggle('is-step-hidden', !active);
    card.setAttribute('aria-hidden', active ? 'false' : 'true');
    if ('inert' in card) {
        card.inert = !active;
    } else if (!active) {
        card.setAttribute('inert', '');
    } else {
        card.removeAttribute('inert');
    }
}

function scrollCreateContainerToTop(options = {}) {
    const createCard = document.getElementById('createMosaicCard');
    if (!createCard) return Promise.resolve();
    return scrollElementToTop(createCard, null, {
        behavior: options.behavior || 'smooth',
        durationMs: Math.max(0, Number(options.durationMs || 260)),
    });
}

function applyCreateFlowStep(step, options = {}) {
    const requested = normalizeCreateFlowStep(step) || 'main';
    const maxAllowedIdx = getCreateFlowMaxAllowedIndex();
    const requestedIdx = getCreateFlowStepIndex(requested);
    const nextIdx = Math.min(requestedIdx, maxAllowedIdx);
    const nextStep = CREATE_FLOW_ORDER[nextIdx] || 'main';
    const previousStep = normalizeCreateFlowStep(_createFlowStep) || 'main';

    _createFlowStep = nextStep;
    CREATE_FLOW_ORDER.forEach((stepName) => {
        const isActive = stepName === nextStep;
        setCreateFlowPanelState(stepName, isActive);
        if (isActive) {
            setCollapsed(stepName, false);
        } else {
            setCollapsed(stepName, true);
        }
    });

    updateCreateFlowHeader();
    updateCreateFlowNextButton();
    animateCreateFlowPanelEntry(previousStep, nextStep, options);

    if (!options.skipLog && previousStep !== nextStep) {
        API.logUiFlow('create_step', 'navigate', 'ok', {
            from_step: previousStep,
            to_step: nextStep,
            requested_step: requested,
            source: String(options.source || ''),
        }).catch(() => {});
    }

    if (options.focus) {
        focusToggle(nextStep);
    }
    return nextStep;
}

function syncCreateFlowFromSelections(options = {}) {
    const current = normalizeCreateFlowStep(_createFlowStep) || 'main';
    const target = resolveCreateFlowStepFromSelections();
    const maxAllowedIdx = getCreateFlowMaxAllowedIndex();
    const currentIdx = getCreateFlowStepIndex(current);

    let next = current;
    if (currentIdx > maxAllowedIdx) {
        next = CREATE_FLOW_ORDER[maxAllowedIdx] || 'main';
    } else if (options.autoAdvance !== false && current !== target) {
        next = target;
    }

    return applyCreateFlowStep(next, {
        source: options.source || 'selection_sync',
        skipLog: options.skipLog !== false,
        focus: Boolean(options.focus),
    });
}

function bindCreateFlowHeader() {
    if (_createFlowHeaderBound) return;
    _createFlowHeaderBound = true;
    CREATE_FLOW_ORDER.forEach((stepName) => {
        const btn = document.getElementById(CREATE_FLOW_BUTTON_BY_STEP[stepName]);
        if (!btn) return;
        btn.addEventListener('click', () => {
            if (isWorkflowTransitionLocked()) return;
            applyCreateFlowStep(stepName, {
                source: 'create_step_header',
                skipLog: false,
                animate: true,
            });
        });
    });
}

function bindCreateFlowNextButton() {
    if (_createFlowNextBound) return;
    _createFlowNextBound = true;
    const nextBtn = document.getElementById('createStepNextBtn');
    if (!nextBtn) return;
    nextBtn.addEventListener('click', () => {
        if (isWorkflowTransitionLocked()) return;
        const nextStep = normalizeCreateFlowStep(nextBtn.dataset.nextStep || '');
        if (!nextStep) return;
        applyCreateFlowStep(nextStep, {
            source: 'create_step_next',
            skipLog: false,
            animate: true,
        });
        scrollCreateContainerToTop({ behavior: 'smooth', durationMs: 240 }).catch(() => {});
    });
}

function emitWorkflowSequenceEvent(phase, metadata = {}) {
    document.dispatchEvent(new CustomEvent('workflow:sequence', {
        detail: {
            phase: String(phase || ''),
            ts: Date.now(),
            ...(metadata || {}),
        },
    }));
}

function setToggleLock(locked) {
    Object.values(CARD_CONFIG).forEach((cfg) => {
        const toggle = document.getElementById(cfg.toggleId);
        if (!toggle) return;
        toggle.disabled = Boolean(locked);
    });
}

function setWorkflowTransitionLock(locked) {
    _workflowTransitionLocked = Boolean(locked);
    setToggleLock(_workflowTransitionLocked);
    if (document.body) {
        document.body.classList.toggle('workflow-transition-locked', _workflowTransitionLocked);
    }
    updateGenerateAvailability(hasMainPhotoSelection(), hasSelectedLibraryWithTiles());
}

export function isWorkflowTransitionLocked() {
    return _workflowTransitionLocked;
}

function setWorkflowScrollInteractionLock(locked) {
    const next = Boolean(locked);
    if (_workflowScrollInteractionLocked === next) return;
    _workflowScrollInteractionLocked = next;
    if (document.body) {
        document.body.classList.toggle('workflow-scroll-lock', next);
    }
}

export function hasMainPhotoSelection() {
    return Boolean(state.selectedSourcePhotoId);
}

function libraryHasTiles(library) {
    if (!library || typeof library !== 'object') return false;
    const tileCount = Number(library.tile_count);
    if (Number.isFinite(tileCount) && tileCount > 0) return true;
    const previews = Array.isArray(library.previews) ? library.previews : [];
    return previews.length > 0;
}

export function hasSelectedLibraryWithTiles() {
    return libraryHasTiles(getSelectedLibrary());
}

export function setSettingsOverride(enabled) {
    state.forceShowSettings = Boolean(enabled);
}

function setText(id, text) {
    const el = document.getElementById(id);
    if (!el) return;
    el.textContent = String(text || '');
}

function getSelectedLibrary() {
    const selectedId = String(state.selectedLibraryId || '').trim();
    if (!selectedId) return null;
    const libs = Array.isArray(state.libraries) ? state.libraries : [];
    return libs.find((lib) => String(lib.id || '') === selectedId) || null;
}

function focusToggle(name) {
    const cfg = CARD_CONFIG[name];
    if (!cfg) return;
    const toggle = document.getElementById(cfg.toggleId);
    if (toggle && typeof toggle.focus === 'function') {
        toggle.focus({ preventScroll: true });
    }
}

function getScrollOffset() {
    const header = document.querySelector('.app-header');
    const headerHeight = header ? header.offsetHeight : 0;
    const main = document.querySelector('.main-content');
    const paddingTop = main ? parseFloat(window.getComputedStyle(main).paddingTop) || 0 : 0;
    return headerHeight + paddingTop;
}

function ensureScrollRoom(targetTop) {
    const scroller = document.scrollingElement || document.documentElement;
    if (!scroller) return;
    const maxScroll = Math.max(0, scroller.scrollHeight - scroller.clientHeight);
    if (targetTop <= maxScroll) return;
    const extraNeeded = targetTop - maxScroll + 8;
    const container = document.querySelector('.container');
    if (!container) return;
    const base = parseFloat(container.dataset.basePadding || '') ||
        (parseFloat(window.getComputedStyle(container).paddingBottom) || 0);
    container.dataset.basePadding = String(base);
    const desired = base + extraNeeded;
    const current = parseFloat(container.style.paddingBottom || '') || base;
    if (desired > current) {
        container.style.paddingBottom = `${desired}px`;
    }
}

export function scrollElementToTop(element, offsetOverride = null, options = {}) {
    if (!element) return Promise.resolve();
    const offset = Number.isFinite(offsetOverride) ? offsetOverride : getScrollOffset();
    const nextTop = element.getBoundingClientRect().top + window.scrollY - offset;
    const targetTop = Math.max(0, nextTop);
    ensureScrollRoom(targetTop);
    const behavior = options.behavior || (prefersReducedMotion() ? 'auto' : 'smooth');
    const requestedDurationMs = Math.max(0, Number(options.durationMs || 0));
    if (behavior === 'smooth' && !prefersReducedMotion()) {
        const durationMs = requestedDurationMs > 0
            ? requestedDurationMs
            : WORKFLOW_SCROLL_DEFAULT_DURATION_MS;
        cancelScrollAnimation();
        return animateWindowScrollTo(targetTop, durationMs);
    }
    cancelScrollAnimation();
    window.scrollTo({ top: targetTop, behavior });
    return Promise.resolve();
}

function scrollCardToTop(name, options = {}) {
    const cfg = CARD_CONFIG[name];
    if (!cfg) return Promise.resolve();
    const card = document.getElementById(cfg.cardId);
    if (!card) return Promise.resolve();
    if (name === 'viewer') {
        const header = document.querySelector('.app-header');
        const headerHeight = header ? header.offsetHeight : 0;
        const gap = getCardGap(card);
        return scrollElementToTop(card, headerHeight + gap, options);
    }
    if (name === 'library' || name === 'settings') {
        const header = document.querySelector('.app-header');
        const headerHeight = header ? header.offsetHeight : 0;
        const gap = getCardGap(card);
        return scrollElementToTop(card, headerHeight + gap, options);
    }
    return scrollElementToTop(card, null, options);
}

function scrollTargetToTop(target, options = {}) {
    if (!target) return Promise.resolve();
    if (typeof target === 'string') {
        return scrollCardToTop(target, options);
    }
    return scrollElementToTop(target, options.offsetOverride, options);
}

export function requestWorkflowScroll(target, options = {}) {
    const token = ++_scrollRequestToken;
    const source = String(options.source || '');
    const targetLabel = typeof target === 'string' ? target : (target && target.id ? target.id : 'custom');
    const targetStage = resolveWorkflowStageForTarget(targetLabel);
    let effectiveTarget = targetLabel;
    const delayMs = Math.max(0, Number(options.delayMs || 0));
    const onDone = (typeof options.onDone === 'function') ? options.onDone : null;
    const finish = () => {
        if (token !== _scrollRequestToken) return;
        setWorkflowScrollInteractionLock(false);
        emitWorkflowSequenceEvent('scroll_end', { source, target: targetLabel });
        if (onDone) {
            try {
                onDone();
            } catch (_e) {}
        }
    };
    const run = () => {
        if (token !== _scrollRequestToken) return;
        setWorkflowScrollInteractionLock(true);
        emitWorkflowSequenceEvent('scroll_start', { source, target: targetLabel });
        Promise.resolve()
            .then(() => {
                if (!targetStage) return false;
                return transitionWorkflowStage(targetStage, {
                    animate: options.stageAnimate !== false,
                    durationMs: Math.max(
                        1,
                        Number(options.stageDurationMs || options.durationMs || WORKFLOW_STAGE_TRANSITION_DEFAULT_MS),
                    ),
                    source: source || 'workflow_scroll',
                    pushHistory: Boolean(options.pushHistory),
                    syncHash: options.syncHash !== false,
                    focus: options.focusStage !== false,
                    focusTargetId: options.focusTargetId || '',
                });
            })
            .then(() => {
                if (typeof target !== 'string') return;
                if (!isCreateFlowStepTarget(targetLabel)) return;
                effectiveTarget = applyCreateFlowStep(targetLabel, {
                    source: source || 'workflow_scroll_target',
                    skipLog: true,
                    focus: false,
                });
            })
            .then(() => {
                if (typeof target === 'string') {
                    return scrollTargetToTop(effectiveTarget, options);
                }
                return scrollTargetToTop(target, options);
            })
            .catch(() => {})
            .finally(() => finish());
    };
    setWorkflowScrollInteractionLock(false);
    if (_scrollRafId) {
        window.cancelAnimationFrame(_scrollRafId);
        _scrollRafId = 0;
    }
    if (_scrollTimeoutId) {
        window.clearTimeout(_scrollTimeoutId);
        _scrollTimeoutId = 0;
    }
    cancelScrollAnimation();
    const runWithDelay = () => {
        if (!delayMs) {
            run();
            return;
        }
        _scrollTimeoutId = window.setTimeout(() => {
            _scrollTimeoutId = 0;
            run();
        }, delayMs);
    };
    if (options.afterFrame) {
        _scrollRafId = window.requestAnimationFrame(() => {
            _scrollRafId = 0;
            runWithDelay();
        });
        return;
    }
    runWithDelay();
}

function cancelScrollAnimation() {
    _scrollAnimToken += 1;
    if (_scrollAnimRafId) {
        window.cancelAnimationFrame(_scrollAnimRafId);
        _scrollAnimRafId = 0;
    }
    if (_scrollAnimResolve) {
        const done = _scrollAnimResolve;
        _scrollAnimResolve = null;
        try {
            done();
        } catch (_e) {}
    }
}

function animateWindowScrollTo(targetTop, durationMs) {
    const startTop = window.scrollY || window.pageYOffset || 0;
    const distance = targetTop - startTop;
    if (Math.abs(distance) < 1) {
        window.scrollTo({ top: targetTop, behavior: 'auto' });
        return Promise.resolve();
    }
    const duration = Math.max(1, Number(durationMs || 1));
    const token = ++_scrollAnimToken;
    return new Promise((resolve) => {
        const startAt = performance.now();
        let settled = false;
        const settle = () => {
            if (settled) return;
            settled = true;
            if (_scrollAnimResolve === settle) {
                _scrollAnimResolve = null;
            }
            resolve();
        };
        _scrollAnimResolve = settle;
        const easeInOutCubic = (t) => (t < 0.5)
            ? 4 * t * t * t
            : 1 - Math.pow(-2 * t + 2, 3) / 2;

        const step = (now) => {
            if (token !== _scrollAnimToken) {
                settle();
                return;
            }
            const elapsed = now - startAt;
            const progress = Math.min(1, elapsed / duration);
            const eased = easeInOutCubic(progress);
            const current = startTop + (distance * eased);
            window.scrollTo({ top: current, behavior: 'auto' });
            if (progress < 1) {
                _scrollAnimRafId = window.requestAnimationFrame(step);
                return;
            }
            _scrollAnimRafId = 0;
            window.scrollTo({ top: targetTop, behavior: 'auto' });
            settle();
        };

        _scrollAnimRafId = window.requestAnimationFrame(step);
    });
}

function applyCollapsedUi(name, collapsed) {
    const cfg = CARD_CONFIG[name];
    if (!cfg) return;
    const card = document.getElementById(cfg.cardId);
    const toggle = document.getElementById(cfg.toggleId);
    const summary = cfg.summaryId ? document.getElementById(cfg.summaryId) : null;
    if (card) card.classList.toggle('is-collapsed', collapsed);
    if (toggle) toggle.setAttribute('aria-expanded', String(!collapsed));
    if (summary) {
        let hideSummary = !collapsed;
        if (name === 'history' && collapsed) {
            const loadedCount = (state.history || []).length;
            hideSummary = loadedCount <= 0;
        }
        summary.classList.toggle('hidden', hideSummary);
    }
}

function setCollapsed(name, collapsed) {
    const cfg = CARD_CONFIG[name];
    if (!cfg) return;
    if (isCreateFlowNonCollapsible(name)) {
        state[cfg.stateKey] = false;
        applyCollapsedUi(name, false);
        return;
    }
    if (name === 'viewer' && !collapsed && !canExpandViewer()) {
        state[cfg.stateKey] = true;
        applyCollapsedUi(name, true);
        return;
    }
    if (name === 'main') {
        state.mainPhotoPending = !collapsed;
    }
    if (name === 'library') {
        state.libraryPending = !collapsed;
    }
    state[cfg.stateKey] = Boolean(collapsed);
    if (name === 'settings' && state[cfg.stateKey]) {
        refreshSettingsCollapsedSummary();
    }
    if (name === 'viewer' && !state[cfg.stateKey]) {
        refreshViewerEmptyState();
    }
    applyCollapsedUi(name, state[cfg.stateKey]);
    if (name === 'viewer') {
        updateViewerClickableState();
    }
    if (state[cfg.stateKey] && (name === 'history' || name === 'library')) {
        scheduleCompactStripRefresh();
    }
    persistCollapsedState();
}

function animateCardHeight(card, startHeight, endHeight, durationMs = CARD_TRANSITION_DURATION_MS) {
    if (!card) return Promise.resolve();
    if (Math.abs(startHeight - endHeight) < 1) return Promise.resolve();
    return new Promise((resolve) => {
        let done = false;
        const prevTransition = card.style.transition;
        const prevHeight = card.style.height;
        const prevOverflow = card.style.overflow;
        const cleanup = () => {
            if (done) return;
            done = true;
            card.removeEventListener('transitionend', onEnd);
            window.clearTimeout(timeoutId);
            card.style.transition = prevTransition;
            card.style.height = prevHeight;
            card.style.overflow = prevOverflow;
            resolve();
        };
        const onEnd = (event) => {
            if (event.target !== card) return;
            if (event.propertyName !== 'height') return;
            cleanup();
        };
        const timeoutId = window.setTimeout(cleanup, Math.max(durationMs + 100, CARD_TRANSITION_FALLBACK_MS));
        card.style.height = `${startHeight}px`;
        card.style.overflow = 'hidden';
        card.style.transition = `height ${durationMs}ms cubic-bezier(0.22, 1, 0.36, 1)`;
        card.getBoundingClientRect();
        card.addEventListener('transitionend', onEnd);
        window.requestAnimationFrame(() => {
            card.style.height = `${endHeight}px`;
        });
    });
}

function setCollapsedWithTransition(name, collapsed, options = {}) {
    const animate = Boolean(options.animate) && !prefersReducedMotion();
    const cfg = CARD_CONFIG[name];
    const card = cfg ? document.getElementById(cfg.cardId) : null;
    if (!animate || !card) {
        setCollapsed(name, collapsed);
        return Promise.resolve();
    }
    const startHeight = card.getBoundingClientRect().height;
    setCollapsed(name, collapsed);
    const endHeight = card.getBoundingClientRect().height;
    return animateCardHeight(card, startHeight, endHeight, options.durationMs || CARD_TRANSITION_DURATION_MS);
}

function waitMs(ms) {
    const timeout = Math.max(0, Number(ms || 0));
    if (!timeout) return Promise.resolve();
    return new Promise((resolve) => {
        window.setTimeout(resolve, timeout);
    });
}

function canExpandViewer() {
    if (state.viewerForceOpen) return true;
    if (state.selectedHistoryJobId) return true;
    const viewerContent = document.getElementById('viewerContent');
    if (viewerContent && !viewerContent.classList.contains('hidden')) return true;
    const progressArea = document.getElementById('progressArea');
    if (progressArea && !progressArea.classList.contains('hidden')) return true;
    const resultCard = document.getElementById('resultCard');
    if (resultCard && resultCard.classList.contains('is-loading')) return true;
    return false;
}

function readCollapsedState() {
    return {};
}

function persistCollapsedState() {
    // Intentionally no-op: collapsed state resets on page refresh.
}

function bindCollapsedCardClick(name) {
    const cfg = CARD_CONFIG[name];
    if (!cfg) return;
    const card = document.getElementById(cfg.cardId);
    if (!card) return;
    card.addEventListener('click', (e) => {
        if (isWorkflowTransitionLocked()) return;
        const target = e.target;
        const closestCard = target && typeof target.closest === 'function' ? target.closest('.card') : null;
        if (closestCard && closestCard !== card) {
            return;
        }
        if (
            target &&
            typeof target.closest === 'function' &&
            target.closest(
                'button, a, input, select, textarea, label, .help-popover, .help-btn, .compare-container, .result-actions, #progressArea, #jobConfig, #triviaCard, .history-item'
            )
        ) {
            return;
        }
        const wasCollapsed = card.classList.contains('is-collapsed');
        if (wasCollapsed) {
            setCollapsed(name, false);
        }
        if (name === 'viewer' && !canExpandViewer()) {
            return;
        }
        if (wasCollapsed || name !== 'viewer') {
            requestWorkflowScroll(name, { source: 'card_click', pushHistory: true });
        }
    });
}

let outsideEngagementBound = false;

function bindOutsideEngagement() {
    if (outsideEngagementBound) return;
    outsideEngagementBound = true;

    document.addEventListener('click', (event) => {
        const target = event.target;

        if (state.mainPhotoPending) {
            const mainCard = document.getElementById('mainPhotoCard');
            if (mainCard && !mainCard.contains(target)) {
                state.mainPhotoPending = false;
                state.mainPhotoEngaged = true;
                refreshWorkspaceSelectionState();
            }
        }

        if (state.libraryPending) {
            const libraryCard = document.getElementById('tileLibraryCard');
            if (libraryCard && !libraryCard.contains(target)) {
                state.libraryPending = false;
                state.libraryEngaged = true;
                refreshWorkspaceSelectionState();
            }
        }

    });
}

function bindSettingsEngagement() {
    const body = document.getElementById('settingsBody');
    if (!body) return;
    const engage = () => {
        if (!state.settingsEngaged) {
            state.settingsEngaged = true;
            state.settingsPrompt = false;
            refreshWorkspaceSelectionState();
        }
    };
    body.addEventListener('pointerdown', engage);
    body.addEventListener('focusin', engage);
    body.addEventListener('input', () => {
        refreshSettingsCollapsedSummary();
    });
    body.addEventListener('change', () => {
        refreshSettingsCollapsedSummary();
    });
    document.addEventListener('draft:legacy_mode_changed', () => {
        refreshSettingsCollapsedSummary();
    });
}

function getCompactStripCapacity(strip) {
    if (!strip) return 5;
    const width = strip.clientWidth || strip.getBoundingClientRect().width || 0;
    if (!Number.isFinite(width) || width <= 0) return 5;
    const styles = window.getComputedStyle(strip);
    const gap = parseFloat(styles.columnGap || styles.gap || '') || COMPACT_STRIP_DEFAULT_GAP_PX;
    const tileSize = COMPACT_STRIP_DEFAULT_TILE_PX;
    const safeWidth = Math.max(0, width - 0.5);
    return Math.max(1, Math.floor((safeWidth + gap) / (tileSize + gap)));
}

function appendCompactMoreTile(strip, hiddenCount, label) {
    if (!strip || hiddenCount <= 0) return;
    const moreTile = document.createElement('div');
    moreTile.className = 'history-tile history-more-indicator';
    moreTile.title = `${hiddenCount} more ${label}`;
    moreTile.textContent = `+${hiddenCount}`;
    strip.appendChild(moreTile);
}

function scheduleCompactStripRefresh() {
    if (_compactStripResizeRaf) return;
    _compactStripResizeRaf = window.requestAnimationFrame(() => {
        _compactStripResizeRaf = 0;
        refreshLibraryCollapsedSummary(getSelectedLibrary());
        updateHistoryCollapsedPreview(state.history || []);
    });
}

function bindCompactStripResize() {
    if (_compactStripResizeBound) return;
    _compactStripResizeBound = true;
    window.addEventListener('resize', scheduleCompactStripRefresh, { passive: true });
    if (typeof ResizeObserver === 'function') {
        _compactStripResizeObserver = new ResizeObserver(() => {
            scheduleCompactStripRefresh();
        });
        ['historyCollapsedStrip', 'tileLibraryCollapsedStrip', 'libPreviewCollapsedStrip'].forEach((id) => {
            const el = document.getElementById(id);
            if (el) _compactStripResizeObserver.observe(el);
        });
    }
}

function refreshMainPhotoCollapsedSummary(mainReady) {
    const selectedPhoto = state.selectedSourcePhoto || null;
    const selectedPhotoName =
        (selectedPhoto && (selectedPhoto.original_filename || selectedPhoto.stored_filename)) ||
        state.targetFilename ||
        '';
    const thumb = document.getElementById('mainPhotoCollapsedThumb');
    const thumbUrl =
        (selectedPhoto && (selectedPhoto.thumb_url || selectedPhoto.full_url || selectedPhoto.source_url)) ||
        '';

    if (mainReady) {
        setText('mainPhotoCollapsedText', selectedPhotoName ? `Selected: ${selectedPhotoName}` : 'Main photo selected.');
    } else {
        setText('mainPhotoCollapsedText', 'No photo selected yet.');
    }

    if (thumb && thumbUrl) {
        thumb.src = thumbUrl;
        thumb.classList.remove('hidden');
    } else if (thumb) {
        thumb.removeAttribute('src');
        thumb.classList.add('hidden');
    }
}

function refreshLibraryCollapsedSummary(selectedLibrary) {
    const strip = document.getElementById('tileLibraryCollapsedStrip');
    const nameEl = document.getElementById('tileLibraryCollapsedName');
    if (strip) strip.innerHTML = '';

    if (!selectedLibrary) {
        if (nameEl) nameEl.textContent = 'No library selected yet.';
        return;
    }

    const libraryName = selectedLibrary.name || 'Library';
    if (nameEl) nameEl.textContent = libraryName;
    if (!strip) return;

    const previews = Array.isArray(selectedLibrary.previews) ? selectedLibrary.previews : [];
    const totalCount = Math.max(0, Number(selectedLibrary.tile_count || previews.length || 0));
    const capacity = getCompactStripCapacity(strip);
    const reserveMoreTile = totalCount > capacity;
    const previewSlots = reserveMoreTile ? Math.max(0, capacity - 1) : Math.min(totalCount, capacity);
    let visibleCount = Math.max(0, Math.min(previews.length, previewSlots));
    const renderWithVisibleCount = (count) => {
        strip.innerHTML = '';
        previews.slice(0, count).forEach((url) => {
            const tile = document.createElement('div');
            tile.className = 'history-tile';

            const img = document.createElement('img');
            img.loading = 'lazy';
            img.decoding = 'async';
            img.src = url;
            img.alt = libraryName;
            tile.appendChild(img);
            strip.appendChild(tile);
        });
        const hiddenCount = Math.max(0, totalCount - count);
        appendCompactMoreTile(strip, hiddenCount, 'tiles');
        return hiddenCount;
    };

    let hiddenCount = renderWithVisibleCount(visibleCount);
    while (visibleCount > 0 && strip.scrollWidth > (strip.clientWidth + 0.5)) {
        visibleCount -= 1;
        hiddenCount = renderWithVisibleCount(visibleCount);
    }

    if (visibleCount === 0 && hiddenCount === 0) {
        const placeholder = document.createElement('span');
        placeholder.className = 'collapsed-summary-thumb-placeholder';
        strip.appendChild(placeholder);
    }
}

function refreshSettingsCollapsedSummary() {
    const textEl = document.getElementById('settingsCollapsedText');
    const collapsedBtn = document.getElementById('settingsCollapsedGenerateBtn');
    const genBtn = document.getElementById('generateBtn');
    if (collapsedBtn && genBtn) collapsedBtn.disabled = genBtn.disabled;
    if (!textEl) return;

    const readSimpleNumber = (id, fallback = 0) => {
        const el = document.getElementById(id);
        const value = el ? Number(el.value) : NaN;
        return Number.isFinite(value) ? Math.round(value) : fallback;
    };

    const detailValue = readSimpleNumber('simpleDetail', 55);
    const colorValue = readSimpleNumber('simpleColor', 50);
    const varietyValue = readSimpleNumber('simpleVariety', 50);
    const detailLabelEl = document.getElementById('simpleDetailVal');
    const colorLabelEl = document.getElementById('simpleColorVal');
    const varietyLabelEl = document.getElementById('simpleVarietyVal');
    const detailLabel = detailLabelEl && detailLabelEl.textContent
        ? detailLabelEl.textContent.trim()
        : `${detailValue}`;
    const colorLabel = colorLabelEl && colorLabelEl.textContent
        ? colorLabelEl.textContent.trim()
        : `${colorValue}`;
    const varietyLabel = varietyLabelEl && varietyLabelEl.textContent
        ? varietyLabelEl.textContent.trim()
        : `${varietyValue}`;
    textEl.textContent =
        `Detail: ${detailLabel} · Color match: ${colorLabel} · Variety: ${varietyLabel}`;
}

function updateGenerateAvailability(hasMain, hasLibrary) {
    const progressArea = document.getElementById('progressArea');
    const resultCard = document.getElementById('resultCard');
    const isBusy =
        (progressArea && !progressArea.classList.contains('hidden')) ||
        (resultCard && resultCard.classList.contains('is-loading'));
    const canGenerate = Boolean(hasMain && hasLibrary && !isBusy && !isWorkflowTransitionLocked());
    const genBtn = document.getElementById('generateBtn');
    if (genBtn) {
        genBtn.disabled = !canGenerate;
    }
    const collapsedGen = document.getElementById('settingsCollapsedGenerateBtn');
    if (collapsedGen) {
        collapsedGen.disabled = !canGenerate;
    }
    const genTooltip = document.getElementById('generateTooltip');
    if (genTooltip) genTooltip.classList.toggle('is-disabled', !canGenerate);
    const collapsedTooltip = document.getElementById('collapsedGenerateTooltip');
    if (collapsedTooltip) collapsedTooltip.classList.toggle('is-disabled', !canGenerate);
}

function refreshWorkspaceSelectionState() {
    const mainWorkspace = document.querySelector('.main-photo-workspace');
    if (mainWorkspace) {
        mainWorkspace.classList.toggle('is-focused', Boolean(state.selectedSourcePhotoId));
    }

    const libraryWorkspace = document.querySelector('.tile-library-workspace');
    if (libraryWorkspace) {
        libraryWorkspace.classList.toggle('is-focused', Boolean(state.selectedLibraryId));
    }

    const mainCard = document.getElementById('mainPhotoCard');
    if (mainCard) {
        mainCard.classList.remove('is-selected', 'is-engaged');
    }

    const libraryCard = document.getElementById('tileLibraryCard');
    if (libraryCard) {
        libraryCard.classList.remove('is-selected', 'is-engaged');
    }

    const settingsCard = document.getElementById('configCard');
    if (settingsCard) {
        settingsCard.classList.remove('is-selected', 'is-engaged');
    }
}

export function refreshHistoryEmptyState() {
    const empty = document.getElementById('historyEmpty');
    const error = document.getElementById('historyError');
    const strip = document.getElementById('historyStrip');
    if (!empty) return;

    const bootStatus = String(state.historyBootStatus || 'loading');
    const isError = bootStatus === 'error';
    const isLoaded = bootStatus === 'loaded';

    if (error) {
        error.classList.toggle('hidden', !isError);
    }
    if (!isLoaded) {
        empty.classList.add('hidden');
        return;
    }

    const hasItems = Boolean(
        strip &&
        (strip.querySelector('.history-item:not(.history-create-cta)') || strip.querySelector('.history-create-cta'))
    ) || (state.history || []).length > 0;
    empty.classList.toggle('hidden', hasItems);
}

export function refreshViewerEmptyState() {
    const empty = document.getElementById('viewerEmptyText') || document.getElementById('viewerEmpty');
    const collapsedText = document.getElementById('resultCollapsedText');
    const loadedCount = (state.history || []).length;
    const hasHistory = loadedCount > 0;
    const nextText = hasHistory
        ? 'Select a Mosaic. Or create a new one.'
        : 'Create a Mosaic first.';
    if (empty) {
        empty.textContent = nextText;
    }
    if (collapsedText) {
        collapsedText.textContent = nextText;
    }
    updateViewerClickableState();
}

function updateViewerClickableState() {
    const resultCard = document.getElementById('resultCard');
    const summary = document.getElementById('resultCollapsedSummary');
    const disabled = !canExpandViewer();
    if (resultCard) resultCard.classList.toggle('is-disabled', disabled);
    if (summary) summary.classList.toggle('is-disabled', disabled);
}

function getCardGap(card) {
    if (!card) return 20;
    const parent = card.parentElement;
    if (parent) {
        const styles = window.getComputedStyle(parent);
        const gap = parseFloat(styles.rowGap || styles.gap || '');
        if (Number.isFinite(gap) && gap > 0) return gap;
    }
    const margin = parseFloat(window.getComputedStyle(card).marginBottom);
    return Number.isFinite(margin) && margin > 0 ? margin : 20;
}

export function scrollResultCardToTop() {
    requestWorkflowScroll('viewer', { source: 'viewer_focus', pushHistory: false });
}

export function expandViewer() {
    transitionWorkflowStage('viewer', {
        animate: false,
        source: 'expand_viewer',
        pushHistory: false,
        syncHash: true,
    }).catch(() => {});
    setCollapsed('viewer', false);
}

export function openCreateMosaicForEdit(options = {}) {
    const shouldFocus = options.focus !== false;
    const forceSettings = options.forceSettings === true;
    const requestedStepOption = normalizeCreateFlowStep(options.step || '');
    transitionWorkflowStage('create', {
        animate: options.animateStage !== false,
        source: options.source || 'open_create',
        pushHistory: Boolean(options.pushHistory),
        syncHash: options.syncHash !== false,
    }).catch(() => {});
    setCollapsed('create', false);
    const requestedStep = requestedStepOption || (forceSettings ? 'settings' : resolveCreateFlowStepFromSelections());
    applyCreateFlowStep(requestedStep, {
        source: options.source || 'open_create',
        skipLog: true,
        focus: shouldFocus,
        animate: options.animateStep !== false,
    });
}

export async function revealCreateMosaicMainForOnboarding(options = {}) {
    const durationMs = Math.max(0, Number(options.durationMs || CARD_TRANSITION_DURATION_MS));
    const animate = options.animate !== false;
    const shouldFocus = options.focus === true;
    await transitionWorkflowStage('create', {
        animate,
        durationMs: Math.max(durationMs, WORKFLOW_STAGE_TRANSITION_DEFAULT_MS),
        source: options.source || 'onboarding_reveal_create',
        pushHistory: false,
        syncHash: options.syncHash !== false,
    });
    await setCollapsedWithTransition('create', false, { animate, durationMs });
    applyCreateFlowStep('main', {
        source: options.source || 'onboarding_reveal_create',
        skipLog: true,
        focus: false,
        animate: animate,
    });
    await setCollapsedWithTransition('main', false, { animate, durationMs });
    if (shouldFocus) focusToggle('main');
}

export async function autoCollapseInputCards(options = {}) {
    const source = String(options.source || 'auto_collapse');
    const forceAnimate = Boolean(options.forceAnimate);
    const shouldAnimate = Boolean(options.animate) && (forceAnimate || !prefersReducedMotion());
    const allowScroll = options.scroll !== false;
    const openViewer = options.openViewer !== false;
    const scrollDelayMs = Math.max(0, Number(options.scrollDelayMs || 0));
    const stepDelayMs = Math.max(0, Number(options.stepDelayMs || 0));
    const collapseDurationMs = Math.max(0, Number(options.collapseDurationMs || CARD_TRANSITION_DURATION_MS));
    const viewerScrollDurationMs = Math.max(0, Number(options.viewerScrollDurationMs || 0));
    const focusBeforeCollapse = options.focusBeforeCollapse || null;
    const focusBeforeCollapseDelayMs = Math.max(0, Number(options.focusBeforeCollapseDelayMs || 0));
    const stickyTopCardName = String(options.stickyTopCard || '').trim();
    const stickyTopCard = stickyTopCardName
        ? (CARD_CONFIG[stickyTopCardName]
            ? document.getElementById(CARD_CONFIG[stickyTopCardName].cardId)
            : document.getElementById(stickyTopCardName))
        : null;
    const collapseOrder = Array.isArray(options.collapseOrder) && options.collapseOrder.length
        ? options.collapseOrder
        : ['main', 'library', 'settings', 'create', 'history'];
    if (!shouldAnimate) {
        state.viewerForceOpen = openViewer;
        if (focusBeforeCollapse) {
            requestWorkflowScroll(focusBeforeCollapse, { source: `${source}_focus`, behavior: 'auto' });
        }
        collapseOrder.forEach((name) => setCollapsed(name, true));
        setCollapsed('viewer', !openViewer);
        state.viewerForceOpen = false;
        if (allowScroll && openViewer) {
            requestWorkflowScroll('viewer', {
                source,
                afterFrame: true,
                behavior: 'auto',
                delayMs: scrollDelayMs,
                durationMs: viewerScrollDurationMs,
                stageDurationMs: 520,
            });
        }
        return;
    }

    setWorkflowTransitionLock(true);
    emitWorkflowSequenceEvent('collapse_start', { source });
    try {
        if (focusBeforeCollapse) {
            requestWorkflowScroll(focusBeforeCollapse, { source: `${source}_focus`, afterFrame: true });
            await waitMs(focusBeforeCollapseDelayMs);
        }

        state.viewerForceOpen = openViewer;
        await setCollapsedWithTransition('viewer', !openViewer, { animate: true, durationMs: collapseDurationMs });
        state.viewerForceOpen = false;

        for (let i = 0; i < collapseOrder.length; i += 1) {
            const name = collapseOrder[i];
            const stickyTopBefore = stickyTopCard ? stickyTopCard.getBoundingClientRect().top : null;
            await setCollapsedWithTransition(name, true, { animate: true, durationMs: collapseDurationMs });
            if (stickyTopCard && Number.isFinite(stickyTopBefore)) {
                const stickyTopAfter = stickyTopCard.getBoundingClientRect().top;
                const delta = stickyTopAfter - stickyTopBefore;
                if (Math.abs(delta) > 1) {
                    window.scrollBy({ top: delta, left: 0, behavior: 'auto' });
                }
            }
            if (i < collapseOrder.length - 1 && stepDelayMs > 0) {
                await waitMs(stepDelayMs);
            }
        }
        emitWorkflowSequenceEvent('collapse_end', { source });
        if (allowScroll && openViewer) {
            requestWorkflowScroll('viewer', {
                source,
                afterFrame: true,
                delayMs: scrollDelayMs,
                durationMs: viewerScrollDurationMs,
                stageDurationMs: 520,
            });
        }
    } finally {
        state.viewerForceOpen = false;
        setWorkflowTransitionLock(false);
    }
}

export function updateHistoryCollapsedPreview(historyItems = []) {
    const strip = document.getElementById('historyCollapsedStrip');
    const summary = document.getElementById('historyCollapsedSummary');
    if (!strip) return;
    strip.innerHTML = '';
    const allItems = Array.isArray(historyItems) ? historyItems : [];
    if (allItems.length <= 0) {
        if (summary && state.historyCollapsed) {
            summary.classList.add('hidden');
        }
        refreshHistoryEmptyState();
        refreshViewerEmptyState();
        return;
    }
    const totalVal = Number(state.historyTotal);
    const totalCount = Number.isFinite(totalVal)
        ? Math.max(allItems.length, Math.max(0, totalVal))
        : allItems.length;
    if (summary && state.historyCollapsed) {
        summary.classList.remove('hidden');
    }
    const capacity = getCompactStripCapacity(strip);
    const reserveMoreTile = totalCount > capacity;
    const visibleSlots = reserveMoreTile ? Math.max(0, capacity - 1) : Math.min(totalCount, capacity);
    let visibleCount = Math.max(0, Math.min(allItems.length, visibleSlots));
    const renderWithVisibleCount = (count) => {
        strip.innerHTML = '';
        const items = allItems.slice(0, count);
        items.forEach((job) => {
            const tile = document.createElement('div');
            tile.className = 'history-tile';
            if (state.selectedHistoryJobId && state.selectedHistoryJobId === job.job_id) {
                tile.classList.add('is-selected');
            }

            const img = document.createElement('img');
            img.loading = 'lazy';
            img.decoding = 'async';
            img.src = job.thumb_url || job.img_url || '';
            img.alt = 'Recent mosaic';
            tile.appendChild(img);
            strip.appendChild(tile);
        });
        const hiddenCount = Math.max(0, totalCount - count);
        appendCompactMoreTile(strip, hiddenCount, 'mosaics');
        return hiddenCount;
    };

    let hiddenCount = renderWithVisibleCount(visibleCount);
    while (visibleCount > 0 && strip.scrollWidth > (strip.clientWidth + 0.5)) {
        visibleCount -= 1;
        hiddenCount = renderWithVisibleCount(visibleCount);
    }

    refreshHistoryEmptyState();
    refreshViewerEmptyState();
}

export function initWorkflowCollapsibles() {
    const saved = readCollapsedState();
    state.historyCollapsed = Boolean(saved.history) || false;
    state.createMosaicCollapsed = Boolean(saved.create);
    state.viewerCollapsed = Boolean(saved.viewer);

    state.mainPhotoCollapsed = Boolean(saved.main);
    state.tileLibraryCollapsed = Boolean(saved.library);
    state.settingsCollapsed = Boolean(saved.settings);
    bindCompactStripResize();

    const historyToggle = document.getElementById('historyToggle');
    if (historyToggle) {
        historyToggle.setAttribute('aria-expanded', 'true');
        historyToggle.setAttribute('aria-disabled', 'true');
        historyToggle.disabled = true;
    }

    const viewerToggle = document.getElementById('resultToggle');
    if (viewerToggle) {
        viewerToggle.setAttribute('aria-expanded', 'true');
        viewerToggle.setAttribute('aria-disabled', 'true');
    }

    bindOutsideEngagement();
    bindSettingsEngagement();
    bindCreateFlowHeader();
    bindCreateFlowNextButton();

    state.historyCollapsed = false;
    state.createMosaicCollapsed = true;
    state.viewerCollapsed = false;
    state.mainPhotoCollapsed = false;
    state.tileLibraryCollapsed = true;
    state.settingsCollapsed = true;

    setCollapsed('history', state.historyCollapsed);
    setCollapsed('create', state.createMosaicCollapsed);
    setCollapsed('viewer', state.viewerCollapsed);
    setCollapsed('main', state.mainPhotoCollapsed);
    setCollapsed('library', state.tileLibraryCollapsed);
    setCollapsed('settings', state.settingsCollapsed);
    applyCreateFlowStep('main', { source: 'init', skipLog: true, focus: false });
    initWorkflowStageState();
    refreshHistoryEmptyState();
    refreshViewerEmptyState();
}

export function refreshSettingsVisibility() {
    const mainReady = hasMainPhotoSelection();
    const selectedLibrary = getSelectedLibrary();
    const hasLibrary = libraryHasTiles(selectedLibrary);
    const hasBoth = mainReady && hasLibrary;
    if (state.settingsEngaged || !hasBoth) {
        state.settingsPrompt = false;
    } else if (!state.settingsPrompt && !state.settingsHadSelection) {
        state.settingsPrompt = true;
        state.settingsHadSelection = true;
    }
    refreshMainPhotoCollapsedSummary(mainReady);
    refreshLibraryCollapsedSummary(selectedLibrary);
    refreshSettingsCollapsedSummary();

    refreshWorkspaceSelectionState();

    const targetDrop = document.getElementById('targetDrop');
    if (targetDrop) targetDrop.classList.remove('source-selected');

    const allowSettings = mainReady && (hasLibrary || state.forceShowSettings);

    if (!mainReady) {
        setCollapsed('settings', true);
    }

    if (!allowSettings) {
        setCollapsed('settings', true);
    }

    if (state.forceShowSettings && allowSettings) {
        applyCreateFlowStep('settings', {
            source: 'force_show_settings',
            skipLog: true,
            focus: !state.createMosaicCollapsed,
        });
        state.forceShowSettings = false;
    } else {
        syncCreateFlowFromSelections({
            source: 'refresh_settings_visibility',
            autoAdvance: false,
            skipLog: true,
            focus: false,
        });
    }

    state.lastMainReady = mainReady;
    state.lastLibrarySelected = hasLibrary;
    updateGenerateAvailability(mainReady, hasLibrary);
    refreshViewerEmptyState();
}
